/**
 * The one category picker, shared by both forms (RESP2d).
 *
 * A native `<select>`, matching what both form modals used before this
 * slice. Not an upgrade to a combobox: this slice changes where the
 * options COME FROM, and changing the control at the same time would
 * make every regression ambiguous between the two.
 *
 * ## The rule this component exists to hold
 *
 * A retired area may be KEPT, never CHOSEN.
 *
 * * Creating — retired areas are absent entirely.
 * * Editing a row that holds a retired area — that one option is
 *   present, marked, and selected. Saving without touching it keeps it;
 *   the backend recognises an unchanged value and does not treat it as
 *   a new choice.
 * * Editing after switching away — the retired option is gone, because
 *   it is no longer the current value. That asymmetry is deliberate and
 *   is the half people get wrong: "it was in the list a second ago".
 *
 * The marker is a word in the option text, not a colour or an icon —
 * a native `<option>` cannot carry either, and a person using a screen
 * reader has to hear the difference too.
 */
import React from "react";

import useActivityCategoryCatalog from "./useActivityCategoryCatalog";

/** Suffix shown on a retired area's option. */
export const INACTIVE_OPTION_SUFFIX = " – Inaktiv";

export default function ActivityCategorySelect({
  id,
  value,
  onChange,
  className,
  /** Rendered as the empty choice. Omit to forbid "no category". */
  emptyLabel = "— bitte wählen —",
  allowEmpty = true,
  disabled = false,
}) {
  const { getSelectableCategories, loading, error, refresh } =
    useActivityCategoryCatalog();

  const options = getSelectableCategories(value);

  if (error) {
    return (
      <div className="space-y-1">
        <select id={id} className={className} disabled value="">
          <option value="">Bereiche nicht verfügbar</option>
        </select>
        <p role="alert" className="m-0 text-[12px] text-destructive">
          {error}{" "}
          <button
            type="button"
            onClick={() => refresh()}
            className="underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </p>
      </div>
    );
  }

  return (
    <select
      id={id}
      className={className}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      // Disabled while loading: submitting a category the catalog has
      // not confirmed would mean guessing, and the backend would refuse
      // it anyway — better to say "not yet" than to fail on save.
      disabled={disabled || loading}
    >
      {allowEmpty ? (
        <option value="">{loading ? "Wird geladen …" : emptyLabel}</option>
      ) : null}
      {options.map((c) => (
        <option key={c.activity_category} value={c.activity_category}>
          {c.is_active === false
            ? `${c.label}${INACTIVE_OPTION_SUFFIX}`
            : c.label}
        </option>
      ))}
    </select>
  );
}
